<template>
    <div>
        <h1 class="text-lg font-medium tracking-tight text-iron-700">
            {{ $t("configurator.booking_page_settings_title") }}
        </h1>

        <p class="text-sm text-iron-500 tracking-[-0.01em]">
            {{ $t("configurator.booking_page_settings_description") }}
        </p>

        <div class="mt-8 gap-1 grid items-center">
            <Label for="name">{{
                $t("configurator.booking_page_title")
            }}</Label>
            <Input id="name" v-model="name" placeholder="Event title.." />
        </div>

        <div class="mt-4 gap-1.5 grid w-full items-center">
            <Label>{{ $t("configurator.introduction_text") }}</Label>
            <Description v-model="eventTypeStore.introductionText" />
        </div>

        <div class="mt-4 gap-1.5 grid w-full items-center">
            <Location
                :location-integrations="locationIntegrations"
                :event-type="eventType"
            ></Location>
        </div>

        <div class="mt-4 gap-4 flex">
            <div class="gap-1 grid w-full items-center">
                <Label for="introductionText">
                    {{ $t("configurator.select_language") }}</Label
                >
                <Select
                    :model-value="eventTypeStore.selectedLanguage.id"
                    @update:model-value="updateLanguage"
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Select a language" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectGroup>
                            <SelectItem
                                v-for="language in languages"
                                :key="language.id"
                                :value="language.id"
                            >
                                {{ language.name }}
                            </SelectItem>
                        </SelectGroup>
                    </SelectContent>
                </Select>
            </div>

            <div class="gap-1 grid w-full items-center">
                <Label for="introductionText">
                    {{ $t("configurator.start_day_of_week") }}</Label
                >
                <Select
                    :model-value="eventTypeStore.startDayOfTheWeek.id"
                    @update:model-value="updateStartDayOfTheWeek"
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Select a language" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectGroup>
                            <SelectItem
                                v-for="day in days"
                                :key="day.id"
                                :value="day.id"
                            >
                                {{ day.name }}
                            </SelectItem>
                        </SelectGroup>
                    </SelectContent>
                </Select>
            </div>
        </div>

        <div class="mt-5 gap-2.5 font-medium text-iron-700 flex items-center">
            <Switch
                id="time-preferences"
                v-model:checked="eventTypeStore.hideTimePreferences"
            />
            <Label for="time-preferences">
                {{ $t("configurator.show_time_preferences") }}</Label
            >

            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger>
                        <HelpCircle :size="20" class="text-iron-500" />
                    </TooltipTrigger>
                    <TooltipContent>
                        <p class="p-2 max-w-[220px]">
                            {{ $t("configurator.time_preferences_explained") }}
                        </p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        </div>

        <div class="mt-5">
            <InputWithUrlPrefix
                :team-slug="teamSlug"
                :event-type-id="eventType.id"
                :slug="eventType.slug"
                :public-url="eventType.public_url"
            />
        </div>

        <hr class="my-5" />

        <div class="mt-4 gap-1 grid items-center">
            <Label for="redirectAfterBooking">{{
                $t("configurator.redirect_after_booking")
            }}</Label>
            <Input
                id="redirectAfterBooking"
                v-model="eventTypeStore.redirectAfterBookingUrl"
                placeholder="https://example.test/"
            />
        </div>

        <div v-if="!adnvacedOpened" class="mt-4 flex w-full justify-center">
            <Button variant="outline" size="sm" @click="adnvacedOpened = true">
                {{ $t("configurator.more_language_options") }}</Button
            >
        </div>

        <div v-if="adnvacedOpened">
            <div class="mt-4 flex justify-end">
                <Button variant="outline" size="sm" @click="resetToDefault">
                    {{ $t("configurator.reset_language_to_default") }}
                </Button>
            </div>

            <div class="mt-4 max-w-sm gap-1.5 grid w-full items-center">
                <Label for="notAvail">{{
                    $t("configurator.not_available_text")
                }}</Label>
                <Input
                    id="notAvail"
                    v-model="eventTypeStore.noEventAvailableLocaleText"
                    type="text"
                />
            </div>

            <div class="mt-4 max-w-sm gap-1.5 grid w-full items-center">
                <Label for="pickTime">{{ $t("configurator.pick_time") }}</Label>
                <Input
                    id="pickTime"
                    v-model="eventTypeStore.pickTimeLocaleText"
                    type="text"
                />
            </div>

            <div class="mt-4 max-w-sm gap-1.5 grid w-full items-center">
                <Label for="choosePreferredTime">{{
                    $t("configurator.choose_preferred_time")
                }}</Label>
                <Input
                    id="choosePreferredTime"
                    v-model="eventTypeStore.choosePreferredTime"
                    type="text"
                />
            </div>

            <p class="mb-3 mt-6 text-xl font-bold">
                {{ $t("configurator.confirmation_form") }}
            </p>

            <div class="mt-4 max-w-sm gap-1.5 grid w-full items-center">
                <Label for="pickTime">{{
                    $t("configurator.confirm_booking_text")
                }}</Label>
                <Input
                    id="pickTime"
                    v-model="eventTypeStore.confirmBookingLocaleText"
                    type="text"
                />
            </div>

            <p class="mb-2 mt-5 text-lg font-medium">
                {{ $t("configurator.buttons_input") }}
            </p>

            <div class="mt-4 max-w-sm gap-1.5 grid w-full items-center">
                <Label for="confirmation">{{
                    $t("configurator.confirmation")
                }}</Label>
                <Input
                    id="confirmation"
                    v-model="eventTypeStore.confirmationButtonLocaleText"
                    type="text"
                />
            </div>

            <div class="mt-4 max-w-sm gap-1.5 grid w-full items-center">
                <Label for="goBack">{{ $t("configurator.go_back") }}</Label>
                <Input
                    id="goBack"
                    v-model="eventTypeStore.goBackButtonLocaleText"
                    type="text"
                />
            </div>

            <p class="mb-2 mt-5 text-lg font-medium">
                {{ $t("configurator.rescheduling") }}
            </p>

            <div class="mt-4 max-w-sm gap-1.5 grid w-full items-center">
                <Label for="confirmation">{{
                    $t("configurator.reschedule_text")
                }}</Label>
                <Input
                    id="rescheduleText"
                    v-model="eventTypeStore.reschedulingEventText"
                    type="text"
                />
            </div>

            <div class="mt-4 max-w-sm gap-1.5 grid w-full items-center">
                <Label for="goBack">{{
                    $t("configurator.reschedule_button")
                }}</Label>
                <Input
                    id="rescheduleButton"
                    v-model="eventTypeStore.confirmationRescheduleButtonText"
                    type="text"
                />
            </div>

            <p class="mb-2 mt-5 text-lg font-medium">
                {{ $t("configurator.guests") }}
            </p>

            <div class="mt-4 max-w-sm gap-1.5 grid w-full items-center">
                <Label for="addGuests">{{
                    $t("configurator.add_guests")
                }}</Label>
                <Input
                    id="addGuests"
                    v-model="eventTypeStore.addGuestsLocaleText"
                    type="text"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { EventType } from "../../types/EventType";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ref, watch } from "vue";
import { usePage } from "@inertiajs/vue3";

import { Switch } from "@/components/ui/switch";
import { languages } from "../../languages";
import useDays from "../../useDays";
import { Button } from "@/components/ui/button";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { useEventTypeStore } from "../../stores/eventTypeStore";
import Description from "./components/Description.vue";
import Location from "./components/Location.vue";
import { LocationIntegrations } from "@/types/EventTypes/Types/Location";
import InputWithUrlPrefix from "@/components/ui/url-input-with-prefix/InputWithUrlPrefix.vue";

import { HelpCircle } from "lucide-vue-next";

const props = defineProps<{
    eventType: EventType;
    locationIntegrations: LocationIntegrations;
    teamSlug: string;
}>();

const eventTypeStore = useEventTypeStore();
const { days } = useDays();
const page = usePage();

const adnvacedOpened = ref(false);

const name = ref(props.eventType.name);

watch(name, (newV, oldV) => {
    if (newV !== oldV) {
        eventTypeStore.queueSave({ name: newV });
    }
});

function updateLanguage(lang: string) {
    const l = languages.find((l) => l.id === lang);

    if (l) {
        eventTypeStore.selectedLanguage = l;
    }
}

function resetToDefault() {
    const communicationLang =
        (page.props.user as any)?.customer_communication_language ?? "en-US";
    const langCode = communicationLang.substring(0, 2);
    const defaultLang =
        languages.find((l) => l.id === langCode) ?? languages[0];

    eventTypeStore.selectedLanguage = defaultLang;
    eventTypeStore.noEventAvailableLocaleText = "";
    eventTypeStore.pickTimeLocaleText = "";
    eventTypeStore.choosePreferredTime = "";
    eventTypeStore.confirmBookingLocaleText = "";
    eventTypeStore.confirmationButtonLocaleText = "";
    eventTypeStore.goBackButtonLocaleText = "";
    eventTypeStore.reschedulingEventText = "";
    eventTypeStore.confirmationRescheduleButtonText = "";
}

function updateStartDayOfTheWeek(day: string) {
    const d = days.value.find((d) => d.id === day);

    if (d) {
        eventTypeStore.startDayOfTheWeek = d;
    }
}
</script>
